/**
 * CalendarEventQuickPopover
 *
 * Popover leve (estilo Google Agenda) exibido ao clicar em um evento no grid
 * do Calendário Global. Mostra um resumo do evento ancorado no próprio bloco
 * clicado, permitindo navegar de evento em evento sem abrir modal em primeiro
 * plano. O modal completo (roteado por fonte) só abre via botão "Abrir".
 *
 * RELEASE:
 *   2026-08-09 v1.0.0 - Versão inicial: âncora virtual (DOMRect), resumo do
 *                       evento, ações Abrir/Excluir/Google/Fechar.
 */

import { useMemo } from 'react';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Clock,
  MapPin,
  Video,
  AlignLeft,
  X,
  Pencil,
  Trash2,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Briefcase,
  User as UserIcon,
  CalendarDays,
} from 'lucide-react';
import { sanitizeHtml } from '@/lib/sanitize';
import { cn } from '@/lib/utils';
import { SOURCE_LABELS } from './sourceMeta';
import type { UnifiedCalendarEvent } from '@/hooks/useCalendarUnifiedEvents';

interface CalendarEventQuickPopoverProps {
  /** Evento em exibição (null = popover fechado). */
  event: UnifiedCalendarEvent | null;
  /** Retângulo do elemento clicado no grid (âncora virtual). */
  anchorRect: DOMRect | null;
  /** Fecha o popover. */
  onClose: () => void;
  /** Abre o modal completo da fonte do evento. */
  onOpenFull: (event: UnifiedCalendarEvent) => void;
  /** Exclui o evento (apenas eventos manuais com permissão). */
  onDelete?: (event: UnifiedCalendarEvent) => void;
}

/**
 * Formata o intervalo de datas do evento em pt-BR.
 * Mesma regra usada no CalendarEventInfoDialog.
 */
function formatRange(startIso: string, endIso: string, allDay: boolean): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (allDay) {
    return start.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }
  const date = start.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${fmtTime(start)} – ${fmtTime(end)}`;
}

export function CalendarEventQuickPopover({
  event,
  anchorRect,
  onClose,
  onOpenFull,
  onDelete,
}: CalendarEventQuickPopoverProps) {
  const open = !!event && !!anchorRect;

  /** Permissão de edição (usada para liberar exclusão de evento manual). */
  const canEditManual =
    !!event &&
    event.source_type === 'manual' &&
    (event.user_permission === 'edit' || event.user_permission === 'manage');

  /** Link direto para o dia do evento no Google Agenda. */
  const googleUrl = useMemo(() => {
    if (!event?.google_event_id) return null;
    const d = new Date(event.start_at);
    return `https://calendar.google.com/calendar/u/0/r/day/${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
  }, [event]);

  if (!event || !anchorRect) return null;

  return (
    <Popover open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Âncora virtual: div fixa nas coordenadas do bloco clicado no grid */}
      <PopoverAnchor asChild>
        <div
          aria-hidden
          style={{
            position: 'fixed',
            top: anchorRect.top,
            left: anchorRect.left,
            width: anchorRect.width,
            height: anchorRect.height,
            pointerEvents: 'none',
          }}
        />
      </PopoverAnchor>

      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={12}
        className="w-[360px] max-w-[92vw] p-0 overflow-hidden"
        // Não rouba o foco para permitir clicar direto em outro evento
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Barra de ações (topo, estilo Google Agenda) */}
        <div className="flex items-center justify-end gap-0.5 px-2 py-1.5 border-b border-border/60 bg-muted/30">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Abrir detalhes"
            onClick={() => onOpenFull(event)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>

          {canEditManual && onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              title="Excluir evento"
              onClick={() => onDelete(event)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}

          {googleUrl && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              title="Abrir no Google Agenda"
              onClick={() => window.open(googleUrl, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Fechar"
            onClick={onClose}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Corpo do resumo */}
        <div className="p-3.5 space-y-2.5">
          {/* Título + bolinha da cor do calendário */}
          <div className="flex items-start gap-2.5">
            <span
              className="mt-1.5 inline-block h-3 w-3 rounded-[3px] shrink-0"
              style={{ backgroundColor: event.calendar_color }}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-medium leading-snug break-words">
                {event.title}
              </h3>
              <p className="text-xs text-muted-foreground capitalize mt-0.5">
                {formatRange(event.start_at, event.end_at, event.all_day)}
              </p>
            </div>
          </div>

          {/* Fonte + calendário */}
          <div className="flex items-center gap-2 flex-wrap pl-[22px]">
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
              {SOURCE_LABELS[event.source_type]}
            </Badge>
            <span className="text-[11px] text-muted-foreground truncate">
              {event.calendar_name}
            </span>
          </div>

          {/* Status concluído/cancelado (atividades e agendamentos) */}
          {(event.completed_at || event.cancelled_at) && (
            <div className="flex items-center gap-2 text-xs">
              {event.cancelled_at ? (
                <>
                  <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                  <span className="text-destructive">Cancelado</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                  <span className="text-muted-foreground">Concluído</span>
                </>
              )}
            </div>
          )}

          {/* Local */}
          {event.location && (
            <div className="flex items-start gap-2 text-xs">
              <MapPin className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground break-words">{event.location}</span>
            </div>
          )}

          {/* Link de reunião */}
          {event.meet_link && (
            <div className="flex items-start gap-2 text-xs">
              <Video className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
              <a
                href={event.meet_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline break-all"
              >
                Entrar na reunião
              </a>
            </div>
          )}

          {/* Descrição resumida (HTML sanitizado quando rich text) */}
          {(event.description_html || event.description) && (
            <div className="flex items-start gap-2 text-xs">
              <AlignLeft className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
              {event.description_html ? (
                <div
                  className={cn(
                    'prose prose-sm dark:prose-invert max-w-none text-muted-foreground',
                    '[&_a]:text-primary line-clamp-4 overflow-hidden'
                  )}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description_html) }}
                />
              ) : (
                <p className="text-muted-foreground whitespace-pre-wrap line-clamp-4">
                  {event.description}
                </p>
              )}
            </div>
          )}

          {/* Negócio vinculado */}
          {event.deal_title && (
            <div className="flex items-start gap-2 text-xs">
              <Briefcase className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground truncate">{event.deal_title}</span>
            </div>
          )}

          {/* Cliente vinculado */}
          {event.customer_name && (
            <div className="flex items-start gap-2 text-xs">
              <UserIcon className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground truncate">{event.customer_name}</span>
            </div>
          )}

          {/* Responsável / dono do calendário */}
          {(event.host_user_name || event.calendar_owner_name) && (
            <div className="flex items-start gap-2 text-xs">
              <CalendarDays className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground truncate">
                {event.host_user_name ?? event.calendar_owner_name}
              </span>
            </div>
          )}

          {/* Sincronização com o Google */}
          {event.google_event_id && (
            <div className="flex items-start gap-2 text-xs">
              <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
              <span className="text-muted-foreground">Sincronizado com o Google Agenda</span>
            </div>
          )}

          {/* Ações principais */}
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" className="h-7 text-xs" onClick={() => onOpenFull(event)}>
              Abrir detalhes
            </Button>
            {event.meet_link && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => window.open(event.meet_link!, '_blank', 'noopener,noreferrer')}
              >
                <Video className="h-3.5 w-3.5 mr-1" /> Meet
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default CalendarEventQuickPopover;
